<template>
  <div class="menu-container relative flex flex-row" ref="menuContainer">
    <!-- Menu Button with animated effects -->
    <button 
      ref="menuButton" 
      class="menu-button mr-3 group" 
      @click="toggleMenu" 
      @mouseenter="animateButton"
      aria-label="Open navigation menu"
    >
      <div class="button-content">
        <div class="icon-container">
          <FontAwesomeIcon 
            :icon="isMenuOpen ? 'xmark' : 'bars'" 
            class="menu-icon transition-transform duration-300 group-hover:scale-110" 
          />
          <div class="icon-pulse"></div>
        </div>
        <span class="menu-text">Menu</span>
      </div>
    </button>
    
    <!-- Quote Button -->
    
    <!-- Enhanced Side Panel with Glass-morphism -->
    <transition name="slide">
      <div v-if="isMenuOpen" class="side-panel">
        <!-- Decorative Panel Elements -->
        <div class="panel-pattern"></div>
        <div class="panel-accent top"></div>
        <div class="panel-accent bottom"></div>
        <div class="metal-grain"></div>
        
        <!-- Dynamic Background Gradients -->
        <div class="dynamic-gradient" ref="dynamicGradient"></div>
        
        <!-- Steel Corner Accents -->
        <div class="corner-accent top-left"></div>
        <div class="corner-accent top-right"></div>
        <div class="corner-accent bottom-left"></div>
        <div class="corner-accent bottom-right"></div>
        
        <div class="panel-content">
          <!-- Primary Navigation Links -->
          <div class="primary-nav">
            <div class="nav-header">
              <div class="company-logo">
                <img src="/img/STE Logo.png" alt="STE Trailers" class="logo-image">
                <div class="logo-glow"></div>
              </div>
              <div class="menu-title">
                <span>Navigation</span>
                <div class="title-accent"></div>
              </div>
            </div>
            
            <div class="nav-sections">
              <div 
                v-for="(section, index) in navigationSections" 
                :key="section.id"
                class="nav-section"
                :class="{ 'active': selectedSection === section.id }"
                :style="{ '--animation-delay': `${index * 0.1}s` }"
                @click="navigateToSection(section)"
              >
                <div class="section-indicator"></div>
                <span class="section-name">{{ section.name }}</span>
                <div class="hover-effect"></div>
              </div>
            </div>
          </div>
          
          <!-- Secondary Navigation - Trailers Submenu -->
          <div class="secondary-nav" v-if="selectedSection === 'slide3'">
            <div class="submenu-header">
              <h3>Trailer Models</h3>
              <div class="header-accent"></div>
            </div>
            
            <div class="trailer-grid">
              <div 
                v-for="(trailer, index) in trailerModels" 
                :key="index"
                class="trailer-item"
                :style="{ '--delay': `${index * 0.1}s` }"
                @mouseenter="activateTrailerItem(index)"
                @mouseleave="deactivateTrailerItem()"
              >
                <div class="trailer-icon">
                  <FontAwesomeIcon :icon="trailer.icon" />
                </div>
                <div class="trailer-info">
                  <h4>{{ trailer.name }}</h4>
                  <p>{{ trailer.description }}</p>
                </div>
                <div class="trailer-bg"></div>
              </div>
            </div>
          </div>
          
          <!-- Quick Links Section -->
          <div class="quick-links">
            <div class="links-header">
              <h3>Quick Links</h3>
              <div class="header-accent"></div>
            </div>
            
            <div class="links-grid">
              <div 
                v-for="(link, index) in quickLinks" 
                :key="index" 
                class="quick-link"
                :style="{ '--link-delay': `${index * 0.1}s` }"
                @click="handleQuickLinkClick(link)"
              >
                <FontAwesomeIcon :icon="link.icon" class="link-icon" />
                <span>{{ link.text }}</span>
              </div>
            </div>
          </div>
          
          <!-- Contact Info -->
          <div class="contact-info">
            <div class="info-item">
              <FontAwesomeIcon :icon="['fas', 'phone']" class="info-icon" />
              <a href="tel:8557838721">(855) STE-USA1</a>
            </div>
            <div class="info-item">
              <FontAwesomeIcon :icon="['fas', 'envelope']" class="info-icon" />
              <a href="mailto:sales@ste-usa.com">sales@ste-usa.com</a>
            </div>
          </div>
          
          <!-- Social Media Links -->
          <div class="social-links">
            <a href="https://www.facebook.com/stainlesstankandequipment/" target="_blank" rel="noopener noreferrer" class="social-icon">
              <FontAwesomeIcon :icon="['fab', 'facebook']" />
            </a>
            <a href="https://www.instagram.com/stainlesstanker/?hl=am-et" target="_blank" rel="noopener noreferrer" class="social-icon">
              <FontAwesomeIcon :icon="['fab', 'instagram']" />
            </a>
            <a href="https://www.linkedin.com/company/stainlesstanker/posts/?feedView=all" target="_blank" rel="noopener noreferrer" class="social-icon">
              <FontAwesomeIcon :icon="['fab', 'linkedin']" />
            </a>
          </div>
          
          <!-- Close Button -->
          <button 
            class="close-button" 
            @click="toggleMenu"
            @mouseenter="isCloseHovered = true"
            @mouseleave="isCloseHovered = false"
            aria-label="Close menu"
          >
            <font-awesome-icon 
              :icon="['fas', 'rectangle-xmark']" 
              class="close-icon text-2xl md:text-3xl"
              :class="{ 'rotate': isCloseHovered }" 
            />
            <div class="close-button-bg"></div>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { useRouter } from 'vue-router';

export default {
  setup() {
    const router = useRouter();
    
    const popupQuote = () => {
      router.push('/about-us');
    };

    return {
      popupQuote
    };
  },
  data() {
    return {
      isMenuOpen: false,
      selectedSection: null,
      isAnimating: false,
      isCloseHovered: false,
      activeTrailerItem: null,
      observer: null,
      mousePosition: { x: 0, y: 0 },
      navigationSections: [
        { id: 'slide2', name: 'NEWS', type: 'scroll' },
        { id: 'trailers', name: 'TRAILERS', type: 'route', route: '/trailers' },
        { id: 'about', name: 'ABOUT', type: 'route', route: '/about-us' },
        { id: 'hendricks', name: 'HENDRICKS', type: 'route', route: '/about-us', hash: '#slide5' },
        { id: 'beloit', name: 'BELOIT', type: 'route', route: '/about-us', hash: '#slide6' },
        { id: 'parts-service', name: 'PARTS & SERVICE', type: 'route', route: '/parts-service' },
        { id: 'careers', name: 'CAREERS', type: 'route', route: '/careers' },
        { id: 'slide7', name: 'CONTACT', type: 'scroll' }
      ],
      trailerModels: [
        { 
          name: '3A-Sanitary', 
          icon: ['fas', 'vial'], 
          description: 'For food-grade liquid transport with the highest sanitary standards.'
        },
        { 
          name: 'Food Grade', 
          icon: ['fas', 'apple-whole'], 
          description: 'Designed for safe transport of consumable liquids and food products.'
        },
        { 
          name: 'DOT407 Chemical', 
          icon: ['fas', 'flask'], 
          description: 'Specialized for chemical transport with DOT compliance.'
        },
        { 
          name: 'ASME', 
          icon: ['fas', 'industry'], 
          description: 'Built to ASME standards for industrial applications.'
        },
        { 
          name: '3-A Farm Pickup', 
          icon: ['fas', 'cow'], 
          description: 'Optimized for dairy and farm product collection.'
        },
        { 
          name: 'Fertilizer', 
          icon: ['fas', 'seedling'], 
          description: 'Designed for agricultural liquid fertilizer transport.'
        }
      ],
      quickLinks: [
        { text: 'Parts Store', url: '/parts-service', icon: ['fas', 'gear'] },
        { text: 'Service Request', url: '/parts-service', icon: ['fas', 'wrench'] },
        { text: 'Job Openings', url: '/careers#slide3', icon: ['fas', 'briefcase'] },
        { text: 'Careers', url: '/careers', icon: ['fas', 'user-plus'] }
        // { text: 'Financing', url: '#', icon: ['fas', 'money-bill'] }, // Temporarily hidden until financing page is implemented
      ]
    };
  },
  mounted() {
    this.setupEventListeners();
    
    // Setup intersection observer for animations
    this.setupIntersectionObserver();
    
    // Listen for escape key to close menu
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    this.removeEventListeners();
    
    if (this.observer) {
      this.observer.disconnect();
    }
    
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      
      if (this.isMenuOpen) {
        // Set initial selected section
        this.selectedSection = this.navigationSections[0].id;
        document.body.classList.add('menu-open');
        this.$nextTick(() => {
          window.addEventListener('mousemove', this.handleMouseMove);
        });
      } else {
        document.body.classList.remove('menu-open');
        window.removeEventListener('mousemove', this.handleMouseMove);
      }
    },
    
    handleQuickLinkClick(link) {
      // Close the menu first
      this.toggleMenu();
      
      // Handle different types of links
      if (link.url === '#' || !link.url) {
        // Do nothing for placeholder links
        return;
      } else if (link.url.startsWith('/')) {
        // Internal route - use Vue Router
        this.$router.push(link.url);
      } else {
        // External URL - open in new tab
        window.open(link.url, '_blank', 'noopener,noreferrer');
      }
    },
    
    navigateToSection(section) {
      // Handle different navigation types
      const sectionData = this.navigationSections.find(s => s.id === section || s.id === section.id);
      
      if (!sectionData) return;
      
      this.selectedSection = sectionData.id;
      
      // Handle different navigation types
      if (sectionData.type === 'route') {
        // Close menu and navigate to route
        this.toggleMenu();
        if (sectionData.hash) {
          // Navigate to route with hash
          this.$router.push(sectionData.route + sectionData.hash);
        } else {
          this.$router.push(sectionData.route);
        }
      } else if (sectionData.type === 'external') {
        // Close menu and navigate to external URL
        this.toggleMenu();
        window.open(sectionData.url, '_blank', 'noopener,noreferrer');
      } else if (sectionData.type === 'scroll') {
        // For trailer section, just switch to submenu (keeping original behavior for slide3)
        if (sectionData.id === 'slide3') {
          return;
        }
        
        // For other scroll sections, close menu and scroll to section
        this.toggleMenu();
        
        // Get the container element
        const container = document.querySelector('.overflow-y-auto');
        const element = document.getElementById(sectionData.id);
        
        if (element && container) {
          container.scrollTo({ 
            top: element.offsetTop, 
            behavior: 'smooth' 
          });
        }
      }
    },
    
    animateButton() {
      if (!this.isAnimating) {
        this.isAnimating = true;
        const button = this.$refs.menuButton;
        button.classList.add('animate');
        button.addEventListener('animationend', this.resetAnimation);
      }
    },
    
    resetAnimation() {
      const button = this.$refs.menuButton;
      button.classList.remove('animate');
      this.isAnimating = false;
    },
    
    activateTrailerItem(index) {
      this.activeTrailerItem = index;
    },
    
    deactivateTrailerItem() {
      this.activeTrailerItem = null;
    },
    
    handleMouseMove(e) {
      // Store mouse position for gradient effect
      this.mousePosition = { x: e.clientX, y: e.clientY };
      
      // Update dynamic gradient position if the menu is open
      if (this.isMenuOpen && this.$refs.dynamicGradient) {
        const panel = this.$refs.dynamicGradient.parentElement;
        const rect = panel.getBoundingClientRect();
        
        // Calculate relative position in the panel (0 to 1)
        const relativeX = (e.clientX - rect.left) / rect.width;
        const relativeY = (e.clientY - rect.top) / rect.height;
        
        // Apply the position to the gradient
        this.$refs.dynamicGradient.style.background = `
          radial-gradient(
            circle at ${relativeX * 100}% ${relativeY * 100}%,
            rgba(143, 0, 0, 0.4) 0%,
            rgba(143, 0, 0, 0.1) 30%,
            transparent 70%
          )
        `;
      }
    },
    
    setupEventListeners() {
      // Use passive event listeners for better performance
      window.addEventListener('resize', this.handleResize, { passive: true });
    },
    
    removeEventListeners() {
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('mousemove', this.handleMouseMove);
    },
    
    handleResize() {
      // Close menu if screen becomes large enough for navbar
      if (window.innerWidth > 768 && this.isMenuOpen) {
        this.toggleMenu();
      }
    },
    
    handleKeyDown(e) {
      // Close menu on escape key
      if (e.key === 'Escape' && this.isMenuOpen) {
        this.toggleMenu();
      }
    },
    
    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          } else {
            // Optionally remove the class when out of view
            // entry.target.classList.remove('in-view');
          }
        });
      }, options);
    }
  }
};
</script>

<style scoped>
/* Base Container Styles */
.menu-container {
  display: flex;
  align-items: center;
  padding: 10px 0 10px 10px;
  z-index: 150;
  width: auto;
}

/* Button Styling */
.menu-button, .quote-button {
  position: relative;
  display: flex;
  align-items: center;
  color: #b7b9ba;
  cursor: pointer;
  padding: 6px 10px;
  z-index: 2;
  transition: transform 0.3s ease, color 0.3s ease;
}

.menu-button {
  margin-right: 12px;
  
  @media (max-width: 768px) {
    transform: scale(1.25);
  }
}

.quote-button {
  @media (max-width: 768px) {
    transform: scale(1);
    margin-left: 6vw;
  }
}

.menu-button:hover, .quote-button:hover {
  transform: translateY(-2px);
  color: rgba(143, 0, 0, 0.9);
}

.button-content {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 2;
}

/* Icon Styling */
.icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
}

.menu-icon, .quote-icon {
  font-size: 1rem;
  position: relative;
  z-index: 2;
  will-change: transform;
}

.icon-pulse {
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(143, 0, 0, 0.3) 0%, transparent 70%);
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.3s ease;
}

.menu-button:hover .icon-pulse {
  opacity: 0.7;
  transform: scale(1);
}

.menu-text, .quote-text {
  font-size: 0.9rem;
  transition: transform 0.3s ease;
  
  @media (max-width: 768px) {
    display: none;
  }
}

.menu-button:hover .menu-text, 
.quote-button:hover .quote-text {
  transform: scale(1.05);
}

/* Button Animation */
@keyframes subtle-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.animate {
  animation: subtle-pulse 0.7s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Enhanced Side Panel with Glass-morphism */
.side-panel {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  max-width: 450px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  z-index: 100;
  overflow: hidden;
  background: linear-gradient(135deg, 
    rgba(29, 30, 38, 0.95) 0%, 
    rgba(42, 43, 56, 0.95) 100%
  );
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 
    10px 0 30px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  will-change: transform;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
}

/* Decorative Elements */
.panel-pattern {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(45deg, rgba(143, 0, 0, 0.03) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(143, 0, 0, 0.03) 25%, transparent 25%);
  background-size: 20px 20px;
  opacity: 0.5;
  pointer-events: none;
}

.metal-grain {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect width="300" height="300" fill="none" stroke-width="0.5" stroke="rgba(255,255,255,0.05)"/><line x1="0" y1="0" x2="300" y2="300" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/><line x1="300" y1="0" x2="0" y2="300" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/></svg>');
  opacity: 0.1;
  pointer-events: none;
}

.panel-accent {
  position: absolute;
  left: 0;
  width: 100%;
  height: 2px;
  z-index: 10;
}

.panel-accent.top {
  top: 0;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(143, 0, 0, 0.8), 
    rgba(255, 255, 255, 0.3), 
    rgba(143, 0, 0, 0.8), 
    transparent
  );
}

.panel-accent.bottom {
  bottom: 0;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(143, 0, 0, 0.8), 
    rgba(183, 185, 186, 0.3), 
    rgba(143, 0, 0, 0.8), 
    transparent
  );
}

.dynamic-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 1;
  pointer-events: none;
  z-index: 0;
  transition: opacity 0.4s ease;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(143, 0, 0, 0.4) 0%,
    rgba(143, 0, 0, 0.1) 30%,
    transparent 70%
  );
}

/* Steel Corner Accents */
.corner-accent {
  position: absolute;
  width: 20px;
  height: 20px;
  z-index: 11;
  border-color: rgba(143, 0, 0, 0.8);
  transition: all 0.3s ease;
}

.corner-accent.top-left {
  top: 0;
  left: 0;
  border-top: 3px solid;
  border-left: 3px solid;
}

.corner-accent.top-right {
  top: 0;
  right: 0;
  border-top: 3px solid;
  border-right: 3px solid;
}

.corner-accent.bottom-left {
  bottom: 0;
  left: 0;
  border-bottom: 3px solid;
  border-left: 3px solid;
}

.corner-accent.bottom-right {
  bottom: 0;
  right: 0;
  border-bottom: 3px solid;
  border-right: 3px solid;
}

/* Main Panel Content */
.panel-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 2rem 1.5rem;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(183, 185, 186S, 0.6) rgba(0, 0, 0, 0.2);
  color: rgb(183, 185, 186);
  z-index: 5;
}

.panel-content::-webkit-scrollbar {
  width: 6px;
}

.panel-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
}

.panel-content::-webkit-scrollbar-thumb {
  background-color: rgba(143, 0, 0, 0.6);
  border-radius: 3px;
}

/* Navigation Header */
.nav-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
}

.company-logo {
  position: relative;
  margin-bottom: 1.5rem;
  width: 160px;
  height: 70px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.logo-image {
  max-width: 100%;
  height: auto;
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.5));
}

.logo-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse, rgba(143, 0, 0, 0.3) 0%, transparent 70%);
  filter: blur(10px);
  opacity: 0.5;
  z-index: 1;
  animation: pulse 4s infinite ease-in-out;
}

.menu-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: rgba(183, 185, 186, 0.6);
}

.title-accent {
  width: 40px;
  height: 2px;
  margin-top: 8px;
  background: linear-gradient(90deg, transparent, rgba(143, 0, 0, 0.8), transparent);
}

/* Primary Navigation Sections */
.nav-sections {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 2rem;
}

.nav-section {
  position: relative;
  display: flex;
  align-items: center;
  padding: 1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
  transform: translateX(-20px);
  opacity: 0;
  animation: slide-in 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  animation-delay: var(--animation-delay);
  transition: background 0.3s ease, transform 0.3s ease;
}

.nav-section:hover {
  background: rgba(143, 0, 0, 0.2);
  transform: translateX(5px);
}

.nav-section.active {
  background: rgba(143, 0, 0, 0.3);
  transform: translateX(10px);
}

.section-indicator {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background: #8f0000;
  transform: scaleY(0);
  transform-origin: bottom;
  transition: transform 0.3s ease;
}

.nav-section:hover .section-indicator,
.nav-section.active .section-indicator {
  transform: scaleY(1);
}

.section-name {
  position: relative;
  z-index: 2;
  font-weight: 500;
  transition: transform 0.3s ease, color 0.3s ease;
}

.nav-section:hover .section-name,
.nav-section.active .section-name {
  transform: translateX(8px);
  color: white;
}

.hover-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, rgba(143, 0, 0, 0.1), transparent);
  transform-origin: left;
  transform: scaleX(0);
  transition: transform 0.3s ease;
  z-index: 1;
}

.nav-section:hover .hover-effect {
  transform: scaleX(1);
}

/* Secondary Navigation - Trailer Models */
.secondary-nav {
  background: rgba(21, 21, 28, 0.5);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  transform: translateY(20px);
  opacity: 0;
  animation: fade-up 0.5s 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  border: 1px solid rgba(143, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
}

.secondary-nav::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, transparent 45%, rgba(143, 0, 0, 0.5) 55%);
  z-index: 0;
}

.submenu-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
}

.submenu-header h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.5rem;
}

.header-accent {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #8f0000, rgba(143, 0, 0, 0.3));
}

.trailer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.trailer-item {
  position: relative;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
  transform: translateY(20px);
  opacity: 0;
  animation: fade-up 0.5s forwards;
  animation-delay: var(--delay);
  z-index: 1;
}

.trailer-item:hover {
  background: rgba(255, 255, 255, 0.07);
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.trailer-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(143, 0, 0, 0.2), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
}

.trailer-item:hover .trailer-bg {
  opacity: 1;
}

.trailer-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(143, 0, 0, 0.2);
  border-radius: 50%;
  margin-right: 1rem;
  flex-shrink: 0;
  transition: background 0.3s ease, transform 0.3s ease;
}

.trailer-item:hover .trailer-icon {
  background: rgba(143, 0, 0, 0.5);
  transform: scale(1.1);
}

.trailer-info {
  flex: 1;
}

.trailer-info h4 {
  font-weight: 600;
  color: white;
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
}

.trailer-info p {
  font-size: 0.85rem;
  color: rgba(183, 185, 186, 0.8);
  line-height: 1.4;
}

/* Quick Links Section */
.quick-links {
  margin-bottom: 2rem;
  transform: translateY(20px);
  opacity: 0;
  animation: fade-up 0.5s 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.links-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.links-header h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.5rem;
}

.links-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.quick-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  color: rgb(183, 185, 186);
  transition: all 0.3s ease;
  transform: translateY(15px);
  opacity: 0;
  animation: fade-up 0.4s forwards;
  animation-delay: var(--link-delay);
  cursor: pointer;
}

.quick-link:hover {
  background: rgba(143, 0, 0, 0.2);
  border-color: rgba(143, 0, 0, 0.3);
  color: white;
  transform: translateY(-3px);
}

.link-icon {
  width: 18px;
  height: 18px;
  color: var(--color-secondary);
  transition: transform 0.3s ease;
}

.quick-link:hover .link-icon {
  transform: scale(1.2);
  color: rgba(143, 0, 0, 0.8);
}

/* Contact Info */
.contact-info {
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transform: translateY(20px);
  opacity: 0;
  animation: fade-up 0.5s 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
}

.info-icon {
  color: rgba(143, 0, 0, 0.8);
}

.info-item a {
  color: rgb(183, 185, 186);
  transition: color 0.3s ease;
}

.info-item a:hover {
  color: white;
  text-decoration: underline;
}

/* Social Media Links */
.social-links {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  transform: translateY(20px);
  opacity: 0;
  animation: fade-up 0.5s 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.social-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  color: rgb(183, 185, 186);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.social-icon:hover {
  background: rgba(143, 0, 0, 0.8);
  color: white;
  transform: translateY(-5px);
}

/* Close Button */
.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.3s ease;
  background: rgba(0, 0, 0, 0.3);
  border: none;
  z-index: 20;
}

.close-button:hover {
  transform: scale(1.1);
}

.close-icon {
  position: relative;
  z-index: 3;
  transition: transform 0.3s ease;
}

.close-icon.rotate {
  transform: rotate(90deg);
}

.close-button-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle, rgba(143, 0, 0, 0.8) 0%, rgba(143, 0, 0, 0.6) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 50%;
  transform: scale(0);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.close-button:hover .close-button-bg {
  opacity: 1;
  transform: scale(1);
}

/* Entrance Animations */
@keyframes slide-in {
  from {
    transform: translateX(-20px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fade-up {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

/* Panel Transition Animations */
.slide-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-leave-active {
  transition: all 0.3s cubic-bezier(0.55, 0, 0.1, 1);
}

.slide-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

/* Mobile menu improvements - Hide logo and social media button when menu is open */
@media (max-width: 768px) {
  /* Target logo and social media toggle when menu is open */
  :global(body.menu-open .logo-wrapper),
  :global(body.menu-open .social-media-links .mobile-toggle),
  :global(body.menu-open .logo-section),
  :global(body.menu-open .right-section) {
    opacity: 0;
    visibility: hidden;
    display: none;
    pointer-events: none; /* Ensure they don't block interactions */
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }
  
  /* Hide quote button on small screens regardless of menu state */
  .quote-button-link {
    display: none;
  }
  
  /* Make sure the side panel takes full width on small screens */
  .side-panel {
    width: 100%;
    max-width: 100%;
  }
  
  /* Ensure menu is properly layered above other content */
  .primary-nav {
    z-index: 200;
  }
  
  /* Improve panel header positioning on mobile */
  .panel-header {
    top: 20px;
    right: 20px;
    z-index: 220; /* Increase z-index */
  }
  
  /* Ensure close button is visible and not overlapped */
  .close-button {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 250; /* Significantly higher z-index to ensure it's above everything */
    width: 50px; /* Larger touch target */
    height: 50px; /* Larger touch target */
    background: rgba(0, 0, 0, 0.5); /* More visible background */
  }
  
  /* Add a pseudo-element to create an even larger touch target */
  .close-button::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    z-index: -1;
  }
  
  /* Ensure the icon itself is properly visible and sized for touch */
  .close-icon {
    font-size: 1.5rem !important;
  }
  
  /* Ensure the side panel content doesn't overlap the close button */
  .panel-content {
    padding-top: 70px !important; /* Add more top padding to avoid content hiding under the close button */
  }
}

/* Adding some additional padding on very small screens */
@media (max-width: 380px) {
  .panel-content {
    padding: 2rem 1rem 2rem 1rem;
  }
  
  .nav-section {
    padding: 0.8rem;
  }
}

/* Panel Transition Animations */
</style>
